import React from "react";
import mergeAdvanced from "object-merge-advanced";
import produce from "immer";
import { validateYupSchema, yupToFormErrors, Field } from "formik";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Form from "../form/Form";
import withAuth from "../auth/withAuth";
import { Provider, defaultState } from "./context";
import WorkflowProviderDesigner from "./WorkflowProviderDesigner";
import withDesign from "../design/withDesign";

class WorkflowProvider extends React.Component {
  static defaultProps = {
    urlMap: {}
  };

  loadData = () => {
    if (!this.props.userInfo) return;
    const activeStep = this.getActiveStep();
    let shouldSkip = false;
    if (!activeStep || !this.state.loadData) return;
    this.setState({ loading: true }, () => {
      this.state
        .loadData(activeStep)
        .then(({ component, result: data }) => {
          shouldSkip = this.state.shouldSkip(data);
          if (shouldSkip) return;

          this.setState(
            produce(state => {
              const nextActiveStep = this.getActiveStep(state);
              if (component) nextActiveStep.form = component.component;

              state.loadedValues = mergeAdvanced(
                nextActiveStep.form.defaultValues || {},
                data || {}
              );

              if (process.env.DESIGNER === "yes") {
                const autoFilledData = this.state.autoFillData(
                  data,
                  this.lastAutoFill,
                  nextActiveStep
                );
                this.setInitialValues(autoFilledData, true)(state);
              } else {
                this.setInitialValues(data, true)(state);
              }
            }),
            () => {
              this.setState({ loading: false }, () => {
                if (process.env.DESIGNER === "yes") {
                  this.updateDesignProps();
                }
                this.form.validateForm().then(errs => {
                  this.form.setErrors(errs);
                });
              });
            }
          );
        })
        .then(() => {
          if (shouldSkip) {
            if (this.state.direction !== "back") this.goForward();
            else this.goBack()();
          }
        });
    });
  };

  updateDesignProps = () => {
    if (process.env.DESIGNER === "yes") {
      if (!this.props.setDesignProps) return;

      this.props.setDesignProps({
        form: this.form,
        workflow: this.state,
        setWorkflowComponentValues: (vals, label) => {
          if (!this.form) return;

          const activeStep = this.getActiveStep();
          if (activeStep.locked) return;

          const newVals = mergeAdvanced(
            this.state.currentValues || {},
            vals || {},
            { hardMergeEverything: true }
          );

          this.form.setValues(newVals);
          this.form.setTouched(newVals);
          this.form.validateForm();

          this.lastAutoFill = label;
        }
      });
    }
  };

  componentDidMount = () => {
    this.loadData();
  };

  componentDidUpdate = prevProps => {
    const activeStep = this.getActiveStep();
    if (
      this.props.location !== prevProps.location ||
      this.props.userInfo !== prevProps.userInfo ||
      this.props.steps !== prevProps.steps
    ) {
      this.loadData();
    }

    if (process.env.DESIGNER === "yes") {
      if (activeStep === undefined) {
        const designProps = this.props.getDesignProps() || {};
        const entryCount = Object.entries(designProps).length;

        if (entryCount > 0) this.props.setDesignProps({});
      } else if (this.props.setDesignProps !== prevProps.setDesignProps) {
        this.updateDesignProps();
      }
    }
  };

  getActiveStep = (state = this.state, withKey = false) => {
    const { steps, stepInfo } = state;
    let result;
    let activeStepKey;
    for (let i = 0, len = steps.length; i < len; i++) {
      const step = steps[i];
      const substeps = Array.isArray(step) ? step : [step];
      activeStepKey = substeps.find(
        substep =>
          this.getLink(substep, true, state) ===
          this.props.history.location.pathname
      );
      if (activeStepKey !== undefined) {
        result = stepInfo[activeStepKey];
        break;
      }
    }
    return withKey === true ? [activeStepKey, result] : result;
  };

  getLink = (key, noQuery, state = this.state) => {
    const step = state.stepInfo[key] || {};
    return (
      state.pathRoot +
      (step.href || this.props.urlMap[key] || "") +
      (noQuery ? "" : "?id=" + state.id)
    );
  };

  // replace initialValues with validated values
  setInitialValues = (vals, valid) => state => {
    const activeStep = this.getActiveStep(state);
    if (activeStep === undefined || activeStep.form === undefined) return;

    activeStep.form.initialValues = mergeAdvanced(
      activeStep.form.defaultValues || {},
      vals || {}
    );
    activeStep.form.isInitialValid = valid;
  };

  enableNextStep = activeStep => state => {
    const nextStep = state.stepInfo[activeStep.next];
    if (nextStep !== undefined && nextStep.disabled) nextStep.disabled = false;
  };

  // is only called by formik when isValid === true
  onSubmit = (vals, formik) => {
    const activeStep = this.state.getActiveStep();
    // server side validation
    this.state.onNext(vals, activeStep).then(result => {
      if (result.success) {
        formik.setSubmitting(false);
        formik.setStatus(undefined);
        formik.setErrors({});

        // move to the next step
        if (activeStep.next !== undefined) {
          this.setState(
            produce(state => {
              this.setInitialValues(vals, true)(state);
              this.enableNextStep(activeStep)(state);
            }),
            () => this.goForward()
          );
        } else if (this.state.onComplete) {
          if (typeof this.state.onComplete === "function")
            this.state.onComplete();
          else this.props.history.push(this.state.onComplete);
        }
      } else {
        formik.setSubmitting(false);
        if (typeof result.message === "string")
          formik.setStatus(result.message);
        else formik.setErrors(result.message);
      }
    });
  };

  goBack = formik => () => {
    const activeStep = this.state.getActiveStep();
    if (activeStep === undefined || activeStep.prev === undefined) return;

    this.setState(
      produce(state => {
        if (formik) this.setInitialValues(formik.values, formik.isValid)(state);
        state.direction = "back";
      }),
      () => this.props.history.push(this.getLink(activeStep.prev))
    );
  };

  goTo = (stepKey, formik) => {
    this.setState(
      produce(this.setInitialValues(formik.values, formik.isValid)),
      () => this.props.history.push(this.getLink(stepKey))
    );
  };

  goForward = (state = this.state) => {
    const activeStep = state.getActiveStep();
    if (activeStep.next !== undefined) {
      const nextStep = state.stepInfo[activeStep.next];
      if (nextStep !== undefined && !nextStep.disabled) {
        this.setState(
          {
            direction: "forward"
          },
          () => this.props.history.push(this.getLink(activeStep.next))
        );
      }
    }
  };

  lastStarted = undefined;

  start = wiz => {
    this.lastStarted = wiz;

    if (process.env.DESIGNER === "yes") {
      delete this.lastAutoFill;
    }

    wiz &&
      this.setState(wiz, () => {
        if (wiz.steps.length === 0) {
          if (typeof this.state.onComplete === "function")
            this.state.onComplete();
          else this.props.history.push(this.state.onComplete);
        } else {
          this.props.history.push(
            wiz.stepInfo[wiz.steps[0]].href + "?id=" + wiz.id
          );
        }
      });
  };

  replace = wiz => {
    if (process.env.DESIGNER === "yes") {
      delete this.lastAutoFill;
    }

    wiz && this.setState(wiz);
  };

  restore = () => {
    if (process.env.DESIGNER === "yes") {
      delete this.lastAutoFill;
    }

    this.lastStarted && this.setState(this.lastStarted);
  };

  state = {
    ...defaultState,
    id: this.props.id || undefined,
    pathRoot: this.props.pathRoot || "/",
    steps: this.props.steps || [],
    stepInfo: this.props.stepInfo || {},
    getActiveStep: key => this.getActiveStep(this.state, key),
    onNext: this.props.onNext || Promise.resolve({ success: true }),
    onComplete: this.props.onComplete,
    goBack: this.goBack,
    goTo: this.goTo,
    getLink: this.getLink,
    field: this.props.field || {},
    header: this.props.header || {},
    section: this.props.section || {},
    start: this.start,
    replace: this.replace,
    restore: this.restore,
    autoFillData: this.props.autoFillData,
    loadData: this.props.loadData,
    shouldSkip: this.props.shouldSkip,
    title: this.props.title,
    hideTitle: this.props.hideTitle,
    direction: "forward"
  };

  validate = validationSchema => values => {
    try {
      validateYupSchema(values, validationSchema, true, values);
    } catch (err) {
      return yupToFormErrors(err);
    }

    return {};
  };

  render = () => {
    const activeStep = this.props.userInfo && this.getActiveStep();
    const {
      sections,
      fieldInfo,
      initialValues = {},
      validationSchema,
      ...rest
    } = (activeStep && !activeStep.disabled && activeStep.form) || {};

    if (process.env.DESIGNER === "yes") {
      return (
        <Form
          noFormTag
          {...rest}
          initialValues={initialValues}
          onSubmit={this.onSubmit}
          validate={validationSchema && this.validate(validationSchema)}
          ref={element => (this.form = element)}
        >
          <Provider value={this.state}>{this.props.children}</Provider>
          <Field>
            {({ form, field }) => {
              if (Object.keys(form.values).length === 0) return null;

              if (this.state.loading) return null;

              if (this.state.currentValues !== form.values) {
                setTimeout(() => {
                  this.setState({
                    currentValues: form.values
                  });
                }, 0);
              }
              return null;
            }}
          </Field>
        </Form>
      );
    } else {
      return (
        <Form
          noFormTag
          {...rest}
          initialValues={initialValues}
          onSubmit={this.onSubmit}
          validate={validationSchema && this.validate(validationSchema)}
          ref={element => (this.form = element)}
        >
          <Provider value={this.state}>{this.props.children}</Provider>
        </Form>
      );
    }
  };
}

let WProvider;

if (process.env.DESIGNER === "yes") {
  WProvider = withDesign(
    withStyles({}, { name: "LcWorkflowProvider" })(
      withAuth(withRouter(WorkflowProvider))
    ),
    WorkflowProviderDesigner
  );
} else {
  WProvider = withStyles({}, { name: "LcWorkflowProvider" })(
    withAuth(withRouter(WorkflowProvider))
  );
}

export default WProvider;
